import type { BvBill } from '../types/billTypes';
import { isArchived } from './archiveScope';
import { billVerdict, bucketOf, type BillVerdict } from '../lib/billVerdict';
import { classifyVariance } from '../lib/reportFormulas';

// The badge and the Status filter must agree on what a bill's status IS, so the
// derivation lives here rather than in the page. Extracted for job 81, which
// added a status that is stored rather than derived.

export type BillStatus =
  | 'pending' | 'overcharge' | 'undercharge' | 'matched' | 'warnings' | 'calculated'
  | 'archived';

/**
 * `verdict` is the bill's billVerdict, with its retailer's charge map when the
 * page has loaded one (useBillVerdicts). Omitted, it is worked out without the
 * map. A bill with a hand-entered total but no parsed invoice has no lines to
 * compare, so its whole totals are classified by the same rule.
 */
export function billStatus(bill: BvBill, verdict: BillVerdict | null = billVerdict(bill)): BillStatus {
  // Job 81: checked first. An archived bill is retired, so no derived verdict
  // applies to it — an Overcharge chip on a retired invoice is a number someone
  // will chase.
  if (isArchived(bill)) return 'archived';
  if (!bill.shadow_result) return 'pending';
  const bucket = verdict?.bucket
    ?? (bill.actual_total != null ? bucketOf(classifyVariance(bill.shadow_result.total, bill.actual_total)) : null);
  if (bucket === 'overcharge') return 'overcharge';
  if (bucket === 'undercharge') return 'undercharge';
  if (bucket === 'within_range') return 'matched';
  return (bill.shadow_result.warnings ?? []).length > 0 ? 'warnings' : 'calculated';
}

// Fixed order, so the dropdown reads worst-first rather than alphabetically.
// Archived is last: this array drives the severity sort and archived is not a
// severity.
export const STATUS_ORDER: BillStatus[] = [
  'overcharge', 'undercharge', 'matched', 'warnings', 'calculated', 'pending', 'archived',
];
